'use client'

/**
 * RecentFlightsTicker — R103.31 + R103.32 (2026-06-04, T-Day)
 *
 * Glass-Marquee mit den letzten 20 frisch eingereichten Wertungsfluegen,
 * sortiert nach Flug-Datum DESC. Sitzt zwischen Top-Header und Page-Content,
 * ist auf JEDER oeffentlichen Seite via FglHeader sichtbar. Klick auf ein
 * Ticker-Item fuehrt zur Zeitwertung (/wertung).
 *
 * Layout:
 *  - md+ (Desktop/Tablet): horizontaler Marquee, links→rechts scrollend
 *  - mobile (< md): vertikaler Marquee, oben→unten scrollend
 *
 * Akkordeon-Lasche (R106.4.4 Stephan-Spec + R106.4.7 alle Formate):
 *   ALLE Viewports bekommen rechts oben eine Lasche "HEUTE ▾".
 *   Klick toggelt collapse. Default eingeklappt (Mobile-Default uebernommen),
 *   persistiert via localStorage.
 *
 * Pro Ticker-Item: Datum · Avatar · Klarname · Klassenname · Speed-Pille · Wertungs-km
 *
 * Marquee: CSS-Animation mit doppeltem Inhalt fuer nahtlosen Loop, pausiert
 * bei Hover damit User in Ruhe lesen kann. GPU-beschleunigt via translate3d.
 */

import { useEffect, useState } from 'react'
import Link from 'next/link'
import { ChevronDown, ChevronUp, Trees, Mountain } from 'lucide-react'

const KEY_COLLAPSED = 'fgl_top_ticker_collapsed'

interface TickerItem {
  id: string
  flightDate: string
  submittedAt: string
  classCode: string | null
  terrainMode: string | null
  wertungsDistanceKm: number | null
  wertungsAvgKmh: number | null
  speedBatch: string | null
  pilot: {
    id: string
    name: string
    slug: string | null
    isPublic: boolean
    avatarUrl: string | null
  }
}

// r19.55.58 (Stephan-Spec 2026-07-14): Kurz-Labels statt STANDARD-KLASSE etc.
// analog Kompakt-Tabelle unter /wertung. Plus Badge-Farbcode aus
// ClassBadge in /wertung/page.tsx (CLUB gold, STANDARD blau, OPEN violett).
const CLASS_FULL: Record<string, string> = {
  CLUBCLASS: 'CLUB',
  STANDARD:  'STANDARD',
  OPEN:      'OPEN',
}

const CLASS_LABEL: Record<string, string> = {
  CLUBCLASS: 'Club-Klasse',
  STANDARD:  'Standard-Klasse',
  OPEN:      'Offene Klasse',
}

const CLASS_PARAM: Record<string, string> = {
  CLUBCLASS: 'club',
  STANDARD:  'standard',
  OPEN:      'open',
}

const CLASS_COLOR: Record<string, string> = {
  CLUBCLASS: '#f2c94e', // gold
  STANDARD:  '#60a5fa', // blau
  OPEN:      '#c084fc', // violett
}

// 2026-06-22 Stephan-Spec: Theme-aware via CSS-Variablen + color-mix statt
// hartcodierter Hex-Werte. FLACHLAND auf state-info (blau), HANG auf accent
// (gold), UNCLEAR auf muted. Damit passt der Ticker zu allen Farbschemen
// und nicht mehr nur zum Dark-Theme.
// r19.55.57/59 (Stephan-Spec 2026-07-14): Piktogramm-Chip statt Text-Pille
// mit lucide-Icons (Trees / Mountain) statt Emoji fuer konsistente
// Fernwirkung auf allen Plattformen. Voller Terrain-Name im Tooltip.
type LucideIcon = typeof Trees
const TERRAIN_LABEL: Record<string, { label: string; Icon: LucideIcon | null; tip: string; color: string; bg: string; border: string; param: string }> = {
  SLOPE: {
    label: 'HANG',
    Icon: Mountain,
    tip: 'Hangflug — Klassenwertung Hang öffnen',
    color: 'var(--accent)',
    bg: 'color-mix(in oklch, var(--accent) 14%, transparent)',
    border: 'color-mix(in oklch, var(--accent) 42%, transparent)',
    param: 'slope',
  },
  FLATLAND: {
    label: 'FLACHLAND',
    Icon: Trees,
    tip: 'Flachland — Klassenwertung Flachland öffnen',
    color: 'var(--state-info, var(--accent))',
    bg: 'color-mix(in oklch, var(--state-info, var(--accent)) 14%, transparent)',
    border: 'color-mix(in oklch, var(--state-info, var(--accent)) 42%, transparent)',
    param: 'flatland',
  },
  UNCLEAR: {
    label: 'OFFEN',
    Icon: null,
    tip: 'Geländetyp noch nicht eindeutig klassifiziert',
    color: 'var(--muted)',
    bg: 'color-mix(in oklch, var(--muted) 12%, transparent)',
    border: 'color-mix(in oklch, var(--muted) 34%, transparent)',
    param: 'flatland',
  },
}

// r19.55.57: Distanz-Meilenstein-Pille im Ticker analog /wertung.
// Zeigt hoechste erreichte Stufe (+50/+100/+200/+300/+400/+500).
const DISTANCE_THRESHOLDS = [50, 100, 200, 300, 400, 500] as const
function DistanceMilestoneTickerPill({ distanceKm }: { distanceKm: number | null | undefined }) {
  if (distanceKm == null || distanceKm <= 0) return null
  const earned = DISTANCE_THRESHOLDS.filter(t => distanceKm >= t)
  if (earned.length === 0) return null
  const top = Math.max(...earned)
  const color =
    top >= 500 ? '#8b5cf6' :
    top >= 400 ? '#10b981' :
    top >= 300 ? '#14b8a6' :
    top >= 200 ? '#06b6d4' :
    top >= 100 ? '#3b82f6' :
                 '#0ea5e9'
  const label =
    top >= 500 ? 'Legend (500+ km)' :
    top >= 400 ? 'Quadro (400+ km)' :
    top >= 300 ? 'Triple (300+ km)' :
    top >= 200 ? 'Double (200+ km)' :
    top >= 100 ? 'Century (100+ km)' :
                 'Starter (50+ km)'
  return (
    <span
      className="text-[10px] font-black tabular-nums px-2 py-0.5 rounded-full shrink-0"
      style={{
        background: `color-mix(in oklch, ${color} 16%, transparent)`,
        color: color,
        border: `1px solid color-mix(in oklch, ${color} 50%, transparent)`,
      }}
      data-tip={`${label} — Wertungsstrecke ${distanceKm.toFixed(2)} km`}
    >
      +{top}
    </span>
  )
}

function formatDate(iso: string): string {
  // "2026-06-04" → "04.06."
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (!m) return iso
  return `${m[3]}.${m[2]}.`
}

function getInitial(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
  return parts[0]?.[0]?.toUpperCase() || '?'
}

function TickerItemRow({ it, idx }: { it: TickerItem; idx: number }) {
  // F-Sprint (2026-06-15, Stephan-Spec): jedes Sub-Element ist ein
  // EIGENER Link mit Tooltip — Pilot, Klasse, Geländetyp, km. Vorher war alles
  // ein einziger Link zur /wertung-Seite, jetzt feingranular zum richtigen
  // Ziel pro Klick.
  const terrain = (it.terrainMode && TERRAIN_LABEL[it.terrainMode]) || TERRAIN_LABEL.UNCLEAR
  const classParam = it.classCode ? CLASS_PARAM[it.classCode] : null
  const classLabel = it.classCode ? CLASS_LABEL[it.classCode] : null

  // F-Sprint (2026-06-15, Stephan-Spec): Klassenpille UND Geländetyp-Pille
  // verlinken beide auf den KOMBINIERTEN Filter (Gelände + Klasse) und tragen
  // beide denselben Tooltip „HANG Club-Klasse öffnen". Vorher zeigte die
  // Klassenpille nur die Klasse — Stephan-Wunsch: Tooltip muss beide Achsen
  // benennen, damit der Pilot weiss wohin der Klick fuehrt.
  const combinedHref = `/wertung?terrain=${terrain.param}${classParam ? `&klasse=${classParam}` : ''}`
  const combinedTip = classLabel
    ? `${terrain.label} ${classLabel} öffnen`
    : `${terrain.label} — Klassenwertung öffnen`

  const pilotHref = it.pilot.slug ? `/pilot/${it.pilot.slug}` : combinedHref
  const flightHref = it.pilot.isPublic ? `/flight/${it.id}` : `/flight/${it.id}` // Privacy-Check macht die Detail-Page selbst

  return (
    <div
      key={`${it.id}-${idx}`}
      className="rft-item inline-flex items-center gap-2 shrink-0"
    >
      <span
        className="text-[11px] font-black uppercase tracking-wider tabular-nums shrink-0"
        style={{ color: 'var(--muted)' }}
      >
        {formatDate(it.flightDate)}
      </span>

      {/* Avatar + Klarname zusammen klickbar zur Pilotenseite */}
      <Link
        href={pilotHref}
        className="inline-flex items-center gap-2 shrink-0 group"
        data-tip={`${it.pilot.name} — Pilotenseite öffnen`}
      >
        <span
          className="relative shrink-0 rounded-full overflow-hidden flex items-center justify-center group-hover:ring-2 transition-shadow"
          style={{
            width: 28,
            height: 28,
            background: 'color-mix(in oklch, var(--accent) 10%, transparent)',
            color: 'var(--accent)',
            fontSize: 11,
            fontWeight: 900,
            border: '1px solid color-mix(in oklch, var(--accent) 28%, transparent)',
          }}
        >
          {it.pilot.avatarUrl ? (
            // eslint-disable-next-line @next/next/no-img-element
            <img
              src={it.pilot.avatarUrl}
              alt={it.pilot.name}
              width={28}
              height={28}
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          ) : (
            <span>{getInitial(it.pilot.name)}</span>
          )}
        </span>
        <span
          className="text-[14px] font-black shrink-0 group-hover:underline"
          style={{ color: 'var(--text)', letterSpacing: '0.02em' }}
        >
          {it.pilot.name}
        </span>
      </Link>

      {/* Klassenpille — kombinierter Tooltip „HANG Club-Klasse öffnen",
           Link auf kombinierten Filter Gelände+Klasse.
           r19.55.58 (Stephan-Spec 2026-07-14): Kompakte Pille mit
           Badge-Farbcode (CLUB gold / STANDARD blau / OPEN violett),
           Label gekuerzt. Konsistent zu ClassBadge in /wertung. */}
      {it.classCode && (
        <Link
          href={combinedHref}
          className="text-[9px] font-black uppercase tracking-[.10em] px-1.5 py-0.5 rounded-full shrink-0 hover:scale-[1.05] transition-transform wertung-classes-pill"
          style={{
            background: `color-mix(in oklch, ${CLASS_COLOR[it.classCode] || 'var(--accent)'} 14%, transparent)`,
            color: CLASS_COLOR[it.classCode] || 'var(--accent)',
            border: `1px solid color-mix(in oklch, ${CLASS_COLOR[it.classCode] || 'var(--accent)'} 42%, transparent)`,
            textDecoration: 'none',
          }}
          data-tip={combinedTip}
        >
          {CLASS_FULL[it.classCode] || it.classCode}
        </Link>
      )}

      {/* Geländetyp-Piktogramm-Chip — r19.55.57/59 (Stephan-Spec 2026-07-14):
           Statt Text-Pille "FLACHLAND"/"HANG" nun kleiner Icon-Chip mit
           lucide-react Trees/Mountain SVG-Icons (bessere Fernwirkung als
           Emoji, konsistent auf allen Plattformen).
           Voller Name bleibt im Tooltip. Klick auf kombinierten Filter. */}
      {it.terrainMode && terrain.Icon && (
        <Link
          href={combinedHref}
          className="inline-flex items-center justify-center rounded-full shrink-0 hover:scale-[1.08] transition-transform"
          style={{
            background: terrain.bg,
            border: `1px solid ${terrain.border}`,
            color: terrain.color,
            width: 22,
            height: 22,
            textDecoration: 'none',
          }}
          data-tip={`${terrain.label} · ${combinedTip}`}
          aria-label={terrain.label}
        >
          <terrain.Icon size={13} strokeWidth={2.2} />
        </Link>
      )}

      {/* r19.55.57: Distanz-Meilenstein-Pille (+50/+100/+200/...) analog
           /wertung, kuehle Farbpalette zur Trennung von warmen Speed-Batches. */}
      <DistanceMilestoneTickerPill distanceKm={it.wertungsDistanceKm} />

      {it.speedBatch && (
        <span
          className="text-[10px] font-black tabular-nums px-2 py-0.5 rounded-full shrink-0"
          style={{
            background: 'color-mix(in oklch, var(--state-warning, var(--accent)) 14%, transparent)',
            color: 'var(--state-warning, var(--accent))',
            border: '1px solid color-mix(in oklch, var(--state-warning, var(--accent)) 50%, transparent)',
          }}
          data-tip={`Speed-Batch ${it.speedBatch} — Wertungs-⌀ hat diese km/h-Schwelle erreicht`}
        >
          {it.speedBatch}
        </span>
      )}

      {/* Wertungs-km — klickbar zur Flug-Public-Page (oder Lock bei privat) */}
      {it.wertungsDistanceKm != null && (
        <Link
          href={flightHref}
          className="text-[14px] font-black tabular-nums shrink-0 hover:underline transition-all"
          style={{
            color: 'var(--ticker-value, var(--accent))',
            textShadow: '0 0 14px color-mix(in oklch, var(--ticker-value, var(--accent)) 42%, transparent)',
            textDecoration: 'none',
          }}
          data-tip={
            it.pilot.isPublic
              ? `Flug öffnen: ${it.wertungsDistanceKm.toFixed(2)} km · ⌀ ${it.wertungsAvgKmh?.toFixed(1) ?? '—'} km/h`
              : `Flug ist privat — eingeloggte Piloten können ihn trotzdem öffnen`
          }
        >
          {it.wertungsDistanceKm.toFixed(2)} km
        </Link>
      )}

      <span
        className="shrink-0 mx-1 opacity-30"
        style={{ color: 'var(--accent)' }}
        aria-hidden
      >
        ·
      </span>
    </div>
  )
}

export function RecentFlightsTicker() {
  const [items, setItems] = useState<TickerItem[]>([])
  const [loaded, setLoaded] = useState(false)
  // R106.4.7: Akkordeon ueber alle Viewports. Default eingeklappt damit Hero
  // sofort sichtbar ist. User toggelt via rechte Lasche.
  const [collapsed, setCollapsed] = useState(true)
  const [hydrated, setHydrated] = useState(false)

  useEffect(() => {
    try {
      const v = localStorage.getItem(KEY_COLLAPSED)
      // null = noch nie geklickt → Default true (eingeklappt)
      setCollapsed(v === null ? true : v === '1')
    } catch { /* noop */ }
    setHydrated(true)
  }, [])

  useEffect(() => {
    let cancelled = false
    function load() {
      fetch('/api/public/ticker/recent-flights?limit=20')
        .then(r => r.ok ? r.json() : { data: [] })
        .then(j => { if (!cancelled && j?.data) { setItems(j.data); setLoaded(true) } })
        .catch(() => { if (!cancelled) setLoaded(true) })
    }
    load()
    const t = setInterval(load, 60_000)
    return () => { cancelled = true; clearInterval(t) }
  }, [])

  if (!loaded || items.length === 0) {
    return null
  }

  // Doppelt ausgeben fuer nahtlosen Loop
  const doubled = [...items, ...items]

  // 2026-06-22 Stephan-Spec: theme-aware Container. Vorher hardcoded
  // dunkler Gradient (rgba 15,20,28). var(--award-ink) ist auch konstant
  // dunkel und gibt im Light-Theme schwarz auf schwarzem Text. Loesung:
  // var(--card) als Theme-Background — dunkel in Dark-Themes, hell in
  // Light-Themes. Plus subtiler Akzent-Mix damit der Ticker sich vom
  // Page-Hintergrund leicht abhebt.
  const containerStyle = {
    background:
      'linear-gradient(180deg,' +
      ' color-mix(in oklch, var(--card) 92%, var(--accent)) 0%,' +
      ' color-mix(in oklch, var(--card) 82%, var(--accent)) 100%)',
    backdropFilter: 'blur(20px)',
    WebkitBackdropFilter: 'blur(20px)',
    borderColor: 'color-mix(in oklch, var(--accent) 30%, var(--border))',
    boxShadow:
      'inset 0 -1px 0 color-mix(in oklch, var(--accent) 18%, transparent),' +
      ' 0 6px 18px color-mix(in oklch, var(--award-ink, #000) 22%, transparent)',
  } as const

  const toggleCollapsed = () => {
    const next = !collapsed
    setCollapsed(next)
    try { localStorage.setItem(KEY_COLLAPSED, next ? '1' : '0') } catch { /* noop */ }
  }

  // Bevor Client-Hydration durch ist, NICHTS rendern — verhindert SSR-Mismatch
  // weil die localStorage-Lookup default-true zurueckgibt aber SSR die Default
  // false denken koennte. Sauberes Pattern siehe SSR-002.
  if (!hydrated) return null

  // R106.4.7 — Akkordeon ueber alle Formate. Body-Hoehe je nach Viewport:
  //   md+  horizontaler Marquee → 50 px Body-Hoehe
  //   < md vertikaler Marquee   → 56 px Body-Hoehe
  // Lasche sitzt absolute unten am Container, haengt damit immer
  // direkt unter dem Marquee (open) bzw. unter dem Header (collapsed).

  // Sharable Lasche-Komponente (vermeidet Duplikat zwischen md/mobile)
  const Lasche = ({ bodyHeight }: { bodyHeight: number }) => (
    <button
      type="button"
      onClick={toggleCollapsed}
      aria-expanded={!collapsed}
      aria-label={collapsed ? 'Heute eingereicht einblenden' : 'Heute eingereicht ausblenden'}
      data-tip={collapsed ? 'Heute eingereicht einblenden' : 'Heute eingereicht ausblenden'}
      className="absolute right-3 flex items-center gap-1.5 px-3 py-1 transition-all"
      style={{
        top: collapsed ? 0 : bodyHeight,
        background: 'linear-gradient(180deg, color-mix(in oklch, var(--card) 92%, var(--accent) 8%) 0%, color-mix(in oklch, var(--card) 96%, var(--accent) 4%) 100%)',
        backdropFilter: 'blur(16px)',
        WebkitBackdropFilter: 'blur(16px)',
        color: 'color-mix(in oklch, var(--accent) 75%, var(--text))',
        borderBottomLeftRadius: 10,
        borderBottomRightRadius: 10,
        border: '1px solid color-mix(in oklch, var(--accent) 40%, transparent)',
        borderTop: 'none',
        boxShadow: '0 2px 8px color-mix(in oklch, var(--bg) 50%, transparent)',
        fontFamily: '"Clash Display", var(--font-display), system-ui, sans-serif',
        fontWeight: 700,
        fontSize: 11,
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        lineHeight: 1,
        zIndex: 2,
      }}
    >
      <span>Heute</span>
      {collapsed
        ? <ChevronDown className="w-3.5 h-3.5" />
        : <ChevronUp className="w-3.5 h-3.5" />}
    </button>
  )

  return (
    <>
      {/* Desktop / Tablet — horizontaler Marquee mit Akkordeon */}
      <div
        className="hidden md:block sticky top-[84px] z-[198]"
        style={{ height: collapsed ? 0 : 50 }}
      >
        <div
          className="recent-flights-ticker overflow-hidden border-b transition-[height,opacity] duration-300 ease-out"
          style={{
            ...containerStyle,
            height: collapsed ? 0 : 50,
            opacity: collapsed ? 0 : 1,
            borderBottomWidth: collapsed ? 0 : 1,
          }}
        >
          <div className="rft-marquee flex items-center gap-7 px-4 py-3 whitespace-nowrap will-change-transform">
            {doubled.map((it, i) => (
              <TickerItemRow key={`h-${it.id}-${i}`} it={it} idx={i} />
            ))}
          </div>
        </div>
        <Lasche bodyHeight={50} />
      </div>

      {/* Mobile — vertikaler Marquee mit Akkordeon */}
      <div
        className="block md:hidden sticky top-[48px] z-[198]"
        style={{ height: collapsed ? 0 : 56 }}
      >
        <div
          className="recent-flights-ticker recent-flights-ticker--vertical overflow-hidden border-b transition-[height,opacity] duration-300 ease-out"
          style={{
            ...containerStyle,
            height: collapsed ? 0 : 56,
            opacity: collapsed ? 0 : 1,
            borderBottomWidth: collapsed ? 0 : 1,
          }}
        >
          <div className="rft-marquee-v flex flex-col items-start gap-3 px-3 py-2 will-change-transform">
            {doubled.map((it, i) => (
              <div key={`v-${it.id}-${i}`} className="w-full">
                <TickerItemRow it={it} idx={i} />
              </div>
            ))}
          </div>
        </div>
        <Lasche bodyHeight={56} />
      </div>
    </>
  )
}
